import { useState, type KeyboardEvent } from 'react'

import SearchIcon from '@mui/icons-material/Search'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  Chip,
  Skeleton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Stack,
  TextField,
} from '@mui/material'

import { gcpLogsService, type GcpLogEntry, handleApiError } from '../api'

const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) {
      return 'Invalid date'
    }
    return `${date.toISOString().split('T')[0]} ${date.toTimeString().split(' ')[0]?.slice(0, 8)}`
  } catch {
    return 'Invalid date'
  }
}

const getSeverityColor = (severity: string) => {
  switch (severity.toUpperCase()) {
    case 'EMERGENCY':
    case 'ALERT':
    case 'CRITICAL':
    case 'ERROR':
      return 'error'
    case 'WARNING':
      return 'warning'
    case 'NOTICE':
    case 'INFO':
      return 'info'
    case 'DEBUG':
    default:
      return 'default'
  }
}

const getOneHourAgo = (): Date => {
  const date = new Date()
  date.setHours(date.getHours() - 1)
  return date
}

const formatDateTimeForInput = (date: Date): string => {
  const pad = (n: number) => n.toString().padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const formatLogPayload = (entry: GcpLogEntry): string => {
  if (entry.jsonPayload) {
    return JSON.stringify(entry.jsonPayload, null, 2)
  }
  if (entry.textPayload) {
    return entry.textPayload
  }
  return '(no payload)'
}

type LimitOption = 50 | 100 | 200 | 500

export default function GcpLogsPage() {
  const [entries, setEntries] = useState<GcpLogEntry[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [keyword, setKeyword] = useState('')
  const [startTime, setStartTime] = useState(formatDateTimeForInput(getOneHourAgo()))
  const [endTime, setEndTime] = useState(formatDateTimeForInput(new Date()))
  const [limit, setLimit] = useState<LimitOption>(100)
  const [hasSearched, setHasSearched] = useState(false)
  const [searchedKeyword, setSearchedKeyword] = useState('')

  const handleSearch = async () => {
    if (!keyword.trim()) {
      setError('Please enter a search keyword')
      return
    }

    try {
      setIsLoading(true)
      setError('')
      setHasSearched(true)
      setSearchedKeyword(keyword)

      const params: Parameters<typeof gcpLogsService.queryLogs>[0] = {
        keyword: keyword.trim(),
        limit,
      }
      if (startTime) {
        params.startTime = new Date(startTime).toISOString()
      }
      if (endTime) {
        params.endTime = new Date(endTime).toISOString()
      }
      const response = await gcpLogsService.queryLogs(params)

      setEntries(response.entries)
    } catch (err) {
      setError(handleApiError(err))
      setEntries([])
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        GCP Cloud Logs
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Search GCP Cloud Logging (restricted to WebhooksController for security)
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={2}>
            <TextField
              label="Search Keyword"
              placeholder="e.g., stripe, vital, error, user@example.com"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyPress={handleKeyPress}
              fullWidth
              required
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
              <TextField
                label="Start Time"
                type="datetime-local"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 220 }}
              />
              <TextField
                label="End Time"
                type="datetime-local"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 220 }}
              />
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>Limit</InputLabel>
                <Select
                  value={limit}
                  label="Limit"
                  onChange={(e) => setLimit(e.target.value as LimitOption)}
                >
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={200}>200</MenuItem>
                  <MenuItem value={500}>500</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={isLoading || !keyword.trim()}
                startIcon={<SearchIcon />}
                sx={{ height: 56 }}
              >
                Search
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {isLoading && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Skeleton variant="rectangular" height={100} />
              <Skeleton variant="rectangular" height={100} />
              <Skeleton variant="rectangular" height={100} />
            </Box>
          </CardContent>
        </Card>
      )}

      {!isLoading && hasSearched && !error && entries.length === 0 && (
        <Alert severity="info">No log entries found for keyword "{searchedKeyword}".</Alert>
      )}

      {!isLoading && entries.length > 0 && (
        <Card>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6">
                Results ({entries.length}) for "{searchedKeyword}"
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {entries.map((entry) => (
                <Box
                  key={entry.insertId}
                  sx={{
                    p: 2,
                    borderBottom: 1,
                    borderColor: 'divider',
                    '&:last-child': { borderBottom: 0 },
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      {formatDate(entry.timestamp)}
                    </Typography>
                    <Chip
                      label={entry.severity}
                      color={getSeverityColor(entry.severity)}
                      size="small"
                      variant="outlined"
                    />
                    {entry.resource?.type && (
                      <Chip
                        label={entry.resource.type}
                        size="small"
                        variant="outlined"
                        color="default"
                      />
                    )}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      {entry.logName.split('/').pop()}
                    </Typography>
                  </Stack>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 1.5,
                      bgcolor: 'grey.100',
                      borderRadius: 1,
                      overflow: 'auto',
                      maxHeight: 300,
                      fontSize: '0.75rem',
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {formatLogPayload(entry)}
                  </Box>
                  {entry.labels && Object.keys(entry.labels).length > 0 && (
                    <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                      {Object.entries(entry.labels).map(([key, value]) => (
                        <Chip
                          key={key}
                          label={`${key}: ${value}`}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.65rem' }}
                        />
                      ))}
                    </Stack>
                  )}
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {!hasSearched && !isLoading && (
        <Card sx={{ bgcolor: 'grey.50' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              How it works
            </Typography>
            <Typography variant="body2" paragraph>
              This tool searches GCP Cloud Logging for webhook-related logs only. For security
              reasons, results are restricted to <code>WebhooksController</code> logs to avoid
              exposing sensitive information from other parts of the application.
            </Typography>
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
              Search tips
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 3 }}>
              <Typography component="li" variant="body2">
                Search for webhook providers: <code>stripe</code>, <code>vital</code>,{' '}
                <code>svix</code>
              </Typography>
              <Typography component="li" variant="body2">
                Search for user emails: <code>user@example.com</code>
              </Typography>
              <Typography component="li" variant="body2">
                Search for error messages: <code>error</code>, <code>failed</code>
              </Typography>
              <Typography component="li" variant="body2">
                Search for specific event types or webhook IDs
              </Typography>
            </Box>
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
              Requirements
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 3 }}>
              <Typography component="li" variant="body2">
                This feature only works when the backend is deployed to GCP (production, staging, or
                dev environments)
              </Typography>
              <Typography component="li" variant="body2">
                The application service account must have <code>roles/logging.viewer</code>{' '}
                permission
              </Typography>
              <Typography component="li" variant="body2">
                Local development requires <code>gcloud auth application-default login</code>
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  )
}
